'use client';
import { useEffect } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import { Typography } from '@gds/Typography/Typography';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { useModal } from '@modal/Hooks/UseModal';

import { calculationAtom } from '../../../Atoms/Calculation.atom';
import { currentProductAtom } from '../../../Atoms/CurrentProduct.atom';
import { finCalcDataFieldValueAtom } from '../../../Atoms/FinCalcDataFieldValue.atom';
import { finCalcMessagesAtom } from '../../../Atoms/FinCalcMessages.atom';
import { isCalculatorLoadingAtom } from '../../../Atoms/IsCalculatorLoading.atom';
import { FieldGroup } from '../../../Elements/FieldGroup/FieldGroup';
import type { FinanceHelpModalContent } from '../../../Entities/FinanceHelpModal.entity';
import { Header } from '../../Header/Header';
import { TermsAndConditions } from '../../TermsAndConditions/TermsAndConditions';
import { AdditionalServices } from '../AdditionalServices/AdditionalServices';
import { defaultCalculatorMessages } from '../Calculator.messages';
import { CalculatorSummary } from '../CalculatorSummary';
import { FinanceHelpModal } from '../FinanceHelpModal/FinanceHelpModal';
import { useCalculator } from '../Hooks/UseCalculator';
import { LoadingCalculatorContent } from '../LoadingElements/LoadingCalculatorContent';
import { LoadingCalculatorOverlay } from '../LoadingElements/LoadingCalculatorOverlay';
import { LoadingSummaryContainer } from '../LoadingElements/LoadingSummaryContainer';
import { ProductToggle } from '../ProductToggle/ProductToggle';
import { UniversalCalculatorFields } from './UniversalCalculator.fields';

import styles from '../Calculator.module.css';
export type UniversalCalculatorProps = {
  locale: Locale;
  strings: any;
  vehicleId: string;
  vehicle: Vehicle;
  financeMessages: FinanceHelpModalContent;
  tenant?: Tenant | undefined;
};

const UniversalCalculator = ({
  strings,
  locale,
  vehicleId,
  vehicle,
  financeMessages,
  tenant,
}: UniversalCalculatorProps) => {
  useCalculator(vehicleId);

  const calculation = useAtomValue(calculationAtom)!;
  const setFieldData = useSetAtom(finCalcDataFieldValueAtom('isLoading'));
  const flags = useAtomValue(flagsAtom);
  const { openModal } = useModal();

  const isVehicleInactive = flags.vehicleWithReservedStatus;

  const hasReservedStatus =
    (vehicle?.details?.status === 'reserved' || vehicle?.details?.status === 'inactive') &&
    isVehicleInactive;

  const setfinCalcMessages = useSetAtom(finCalcMessagesAtom);
  setfinCalcMessages(strings ?? defaultCalculatorMessages);

  useEffect(() => {
    setFieldData(false);
  }, []);

  const product = useAtomValue(currentProductAtom);
  const isLoading = useAtomValue(isCalculatorLoadingAtom);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const hasError = calculation === null || !!calculation?.errorCode;
  const hasCaptiveError = calculation?.errorCode === 'CAPTIVE_SERVER_ERROR';
  const isReady = !!calculation && !!product;
  const isInitialLoading = calculation === undefined;

  const handleOpenModal = () => {
    openModal(
      <FinanceHelpModal
        locale={locale}
        financeModalStrings={strings}
        financeMessages={financeMessages}
        vehicle={vehicle}
      />,
      {
        header: strings.financeModalHeader,
        modalClassName: styles.modalContainer,
        modalBodyClassName: styles.modalBody,
      },
    );
  };

  if (!vehicle?.pricing?.hasFinance) return null;

  return (
    <section id="financing-calculator" className={styles.calculatorWrapper}>
      <div className={styles.mainWrapper}>
        <div
          className={`${styles.mainOverlay} ${!!hasCaptiveError || hasError ? styles.visible : ''}`}
        >
          <LoadingCalculatorOverlay strings={strings} />
        </div>
        <form onSubmit={onSubmit} autoComplete="on" id="financing-calculator-form">
          <div className={styles.formContainer}>
            {isInitialLoading ? (
              <LoadingCalculatorContent strings={strings} />
            ) : (
              <section className={styles.content}>
                <Header strings={strings} handleHelpDialog={handleOpenModal} />
                {!hasError && (
                  <>
                    <ProductToggle strings={strings} />
                    {isReady && <FieldGroup fields={UniversalCalculatorFields} />}
                    <AdditionalServices key={'additionalServices'} strings={strings} />

                    {isLoading ? (
                      <LoadingSummaryContainer
                        strings={strings}
                        hasReservedStatus={hasReservedStatus}
                      />
                    ) : (
                      <CalculatorSummary strings={strings} tenant={tenant} />
                    )}
                  </>
                )}
              </section>
            )}
            {hasError && (
              <div className={styles.error}>
                <div>
                  <Typography variant="h5">{strings.errorTitle}</Typography>

                  <Typography variant="body3" Component="p">
                    {strings.errorBody}
                  </Typography>
                </div>
              </div>
            )}
          </div>
        </form>
        <TermsAndConditions strings={strings} />
      </div>
    </section>
  );
};
export default UniversalCalculator;
